import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useLocalStorageJSON } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { DEVICE_PREFERENCES_LOCAL_STORAGE_ID } from 'modules/onboarding-page/constants';
import { DevicesConfig } from 'modules/onboarding-page/types';

import FormSection from './FormSection';
import LargeCheckBox from './LargeCheckBox';
import MobileData from './MobileData';
import Tablet from './Tablet';
import { RFFFieldErrorWrapper } from '../../../../../common/components';
import { Body3 } from '../../../../../common/typography/Typography';
import validateYupSchema from '../../../../../common/utils/validateYupSchema';
import { CountryConfig } from '../../../../country/types';
import { RenderPrice } from '../../RenderPrice';
import { DevicePreferences, DevicePreferencesSchema } from '../../validation';

type DeviceInfoProps = {
  title: string;
  priceComponent: React.ReactNode;
  description: string;
};

const FormSectionWrapper = styled.div`
  margin-block-end: 32px;

  img {
    width: 100%;
  }
`;

const Title = styled(T.Title1)`
  display: block;
  width: 100%;
  font-weight: 700;
  margin-block-start: 32px;
  margin-block-end: 16px;
`;

const CheckboxWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding-inline-start: 16px;
  padding-inline-end: 16px;
  padding-block-end: 16px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 0 16px;
  margin-block-end: 8px;
`;

const DeviceInfoRowWrapper = styled.div`
  > div:nth-child(1) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-block-end: 4px;
  }

  > div:nth-child(2) {
    font-weight: 400;
    line-height: 20px;
  }
`;

const CardDeviceInfoRowWrapper = styled.div`
  > div:nth-child(1) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-block: 16px;

    > span:nth-child(1) {
      font-weight: 500;
    }
  }

  > div:nth-child(2) {
    font-weight: 400;
    line-height: 20px;
  }
`;

const SectionDivider = styled.hr`
  margin: 16px 0;
  border: none;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const NeedDeviceCheckBox = styled(LargeCheckBox)`
  margin-block-start: 16px;
`;

const DeviceInfo = ({ title, priceComponent, description, ...rest }: DeviceInfoProps) => (
  <DeviceInfoRowWrapper {...rest}>
    <div>
      <Body3>{title}</Body3>
      <Body3>{priceComponent} *</Body3>
    </div>
  </DeviceInfoRowWrapper>
);

const CheckboxCardDeviceInfo = ({ title, priceComponent, description, ...rest }: DeviceInfoProps) => (
  <CardDeviceInfoRowWrapper {...rest}>
    <div>
      <Body3>{title}</Body3>
      <Body3>{priceComponent} *</Body3>
    </div>

    <div>
      <Body3>{description}</Body3>
    </div>
  </CardDeviceInfoRowWrapper>
);

type DeviceSelectionFormProps = {
  formId: string;
  devicePreferences: DevicePreferences;
  deviceConfig: DevicesConfig;
  handleSubmit: (values: DevicePreferences) => Promise<void | { [key: string]: string | Error }>;
  countryConfig: CountryConfig;
};

const DevicePreferencesForm = ({
  handleSubmit,
  formId,
  devicePreferences,
  deviceConfig,
  countryConfig,
}: DeviceSelectionFormProps) => {
  const intl = useIntl();

  const [storedDevicePreferences, setDevicePreferences] = useLocalStorageJSON<DevicePreferences>(
    DEVICE_PREFERENCES_LOCAL_STORAGE_ID,
    devicePreferences,
  );

  const { currency, primary_language } = countryConfig;

  return (
    <Form<DevicePreferences>
      initialValues={{
        needsPrinter: storedDevicePreferences.needsPrinter || false,
        needsMobileDataPackage: storedDevicePreferences.needsMobileDataPackage || false,
      }}
      onSubmit={handleSubmit}
      validate={validateYupSchema(DevicePreferencesSchema)}
    >
      {({ handleSubmit }) => (
        <>
          <FormSpy
            subscription={{ values: true }}
            onChange={(props): void => {
              setDevicePreferences(props.values as DevicePreferences);
            }}
          />
          <form id={formId} onSubmit={handleSubmit}>
            <Title>{intl.formatMessage({ id: 'self-serve-sign-up.wizard.device-preferences.title' })}</Title>
            {deviceConfig.tabletConfiguration && (
              <FormSection>
                <Tablet
                  style={{
                    width: '24px',
                    marginBottom: '8px',
                  }}
                />
                <DeviceInfo
                  title={intl.formatMessage({ id: 'self-serve-sign-up.wizard.device-preferences.wolt-tablet.title' })}
                  priceComponent={
                    <RenderPrice
                      primaryLanguage={primary_language}
                      value={deviceConfig.tabletConfiguration.tabletCost}
                      currency={currency}
                    />
                  }
                  description=""
                />
                {deviceConfig.mobileDataOption && deviceConfig.mobileDataOption.mandatory && (
                  <div>
                    <SectionDivider />
                    <MobileData
                      style={{
                        width: '24px',
                        marginBottom: '8px',
                      }}
                    />
                    <Field<boolean> name="needsMobileDataPackage" type="hidden" component="input" initialValue />
                    <DeviceInfo
                      title={intl.formatMessage({
                        id: 'self-serve-sign-up.wizard.device-preferences.mobile-data.mandatory-title',
                      })}
                      priceComponent={
                        <RenderPrice
                          primaryLanguage={primary_language}
                          value={deviceConfig.mobileDataOption!.cost}
                          currency={currency}
                          isPricePerWeek
                        />
                      }
                      description=""
                    />
                  </div>
                )}
              </FormSection>
            )}
            {(deviceConfig.printerOptionConfiguration || deviceConfig.mobileDataOption) && (
              <Title>
                {intl.formatMessage({ id: 'self-serve-sign-up.wizard.device-preferences.additional-options' })}
              </Title>
            )}
            {deviceConfig.mobileDataOption && !deviceConfig.mobileDataOption.mandatory && (
              <FormSectionWrapper>
                <FormSection wrapContentWithLabel>
                  <img
                    src={`${process.env.CDN_URL}/onboarding/ssmo/service-pricing/printer.png`}
                    alt="printer banner"
                  />

                  <Field<boolean | undefined> name="needsMobileDataPackage" type="checkbox">
                    {field => (
                      <>
                        <CheckboxWrapper>
                          <RFFFieldErrorWrapper field={field}>
                            {input => (
                              <NeedDeviceCheckBox checked={input.checked} onChange={input.onChange} id={input.name} />
                            )}
                          </RFFFieldErrorWrapper>
                        </CheckboxWrapper>
                        <CheckboxCardDeviceInfo
                          title={intl.formatMessage({
                            id: 'self-serve-sign-up.wizard.device-preferences.mobile-data.title',
                          })}
                          priceComponent={
                            <RenderPrice
                              primaryLanguage={primary_language}
                              value={deviceConfig.mobileDataOption!.cost}
                              currency={currency}
                              isPricePerWeek
                            />
                          }
                          description={intl.formatMessage({
                            id: 'self-serve-sign-up.wizard.device-preferences.mobile-data.description',
                          })}
                        />
                      </>
                    )}
                  </Field>
                </FormSection>
              </FormSectionWrapper>
            )}
            {deviceConfig.printerOptionConfiguration && (
              <FormSectionWrapper>
                <FormSection wrapContentWithLabel>
                  <img
                    src={`${process.env.CDN_URL}/onboarding/ssmo/service-pricing/mobile-data.png`}
                    alt="mobile data banner"
                  />

                  <Field<boolean | undefined> name="needsPrinter" type="checkbox">
                    {field => (
                      <>
                        <CheckboxWrapper>
                          <RFFFieldErrorWrapper field={field}>
                            {input => (
                              <NeedDeviceCheckBox checked={input.checked} onChange={input.onChange} id={input.name} />
                            )}
                          </RFFFieldErrorWrapper>
                        </CheckboxWrapper>

                        <CheckboxCardDeviceInfo
                          title={intl.formatMessage({
                            id: 'self-serve-sign-up.wizard.device-preferences.printer.title',
                          })}
                          priceComponent={
                            <RenderPrice
                              primaryLanguage={primary_language}
                              value={deviceConfig.printerOptionConfiguration!.cost}
                              currency={currency}
                            />
                          }
                          description={intl.formatMessage({
                            id: 'self-serve-sign-up.wizard.device-preferences.printer.description',
                          })}
                        />
                      </>
                    )}
                  </Field>
                </FormSection>
              </FormSectionWrapper>
            )}

            <Body3>
              *{' '}
              {intl.formatMessage({
                id: 'self-serve-sign-up.wizard.device-preferences.fee-explanation',
              })}
            </Body3>
          </form>
        </>
      )}
    </Form>
  );
};

export default DevicePreferencesForm;
